// Counts Esri tile starts per page (spec §8.2). Sixty one-minute buckets
// make a rolling hour; a trip fires once and the page swaps to NSW tiles.
export interface TileMeter {
  count(): void;
  total(): number;
  drain(): number;
  tripped(): boolean;
  onTrip(cb: () => void): () => void;
}

const MINUTE = 60_000;

export function createTileMeter(opts: { perHour: number; now?: () => number }): TileMeter {
  const now = opts.now ?? ((): number => Date.now());
  const buckets = new Array<number>(60).fill(0);
  const stamps = new Array<number>(60).fill(-1); // minute index each bucket holds
  let sinceDrain = 0;
  let tripped = false;
  const listeners = new Set<() => void>();

  function minute(): number {
    return Math.floor(now() / MINUTE);
  }
  function slot(m: number): number {
    return m % 60;
  }
  /** Claim this minute's bucket, clearing it if it still holds an older one. */
  function touch(): number {
    const m = minute();
    const s = slot(m);
    if (stamps[s] !== m) {
      stamps[s] = m;
      buckets[s] = 0;
    }
    return s;
  }
  function total(): number {
    const m = minute();
    let sum = 0;
    for (let i = 0; i < 60; i += 1) if (stamps[i]! > m - 60) sum += buckets[i]!;
    return sum;
  }
  return {
    count() {
      const s = touch();
      buckets[s] = buckets[s]! + 1;
      sinceDrain += 1;
      if (!tripped && total() >= opts.perHour) {
        tripped = true;
        // Warned here rather than in every host, so one trip says it once.
        console.warn("Tile guard tripped: swapping to NSW basemaps for this page");
        for (const l of listeners) l();
      }
    },
    total,
    drain() {
      const n = sinceDrain;
      sinceDrain = 0;
      return n;
    },
    tripped: () => tripped,
    onTrip(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };
}

/** The page's budget. An unset, blank or nonsense var reads as the default —
 *  `Number("")` is 0, which would trip the guard on the very first tile. */
export function tileBudget(raw: string | undefined): number {
  const n = raw === undefined || raw.trim() === "" ? NaN : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 5000;
}

function fromEnv(perHour?: number): TileMeter {
  return createTileMeter({
    perHour: perHour ?? tileBudget(import.meta.env.VITE_TILE_GUARD_PER_HOUR),
  });
}

// The singleton is a thin delegate over a replaceable inner meter, so a test
// can put the page's meter back to untripped (a trip is otherwise permanent).
let inner = fromEnv();

export const tileMeter: TileMeter = {
  count: () => inner.count(),
  total: () => inner.total(),
  drain: () => inner.drain(),
  tripped: () => inner.tripped(),
  onTrip: (cb) => inner.onTrip(cb),
};

/** Test-only: throw away the singleton's counts, trip flag and listeners.
 *  `perHour` overrides the budget so a test can trip it in a few calls. */
export function _resetForTests(perHour?: number): void {
  inner = fromEnv(perHour);
}
